import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import LearningResultsView from '../../LearningResultsView';
import { touchFriendly, responsiveSpacing } from '../../../styles/responsive';
import { SHADOWS } from '../../../styles/theme';
import { ParkingTestState } from '../types';

interface HistoryListItemProps {
  item: any;
  index: number;
  isSelected: boolean;
  onSelect: (item: any) => void;
  variant: 'desktop' | 'mobile';
}

const HistoryListItem: React.FC<HistoryListItemProps> = ({
  item,
  index,
  isSelected,
  onSelect,
  variant,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <ListItem key={item.id || index} disablePadding>
      <ListItemButton
        selected={isSelected}
        onClick={() => onSelect(item)}
        className={variant === 'desktop' ? 'hover-lift' : undefined}
        sx={variant === 'desktop' ? {
          transition: 'all 0.2s ease',
          '&:hover': {
            bgcolor: alpha(theme.palette.action.hover, 0.05)
          },
          '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1),
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, isDark ? 0.2 : 0.15)
            }
          }
        } : {
          ...touchFriendly.button,
          py: 2,
          px: 2
        }}
      >
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography
                variant={variant === 'desktop' ? 'subtitle2' : 'subtitle1'}
                fontWeight="bold"
                sx={variant === 'mobile' ? { fontSize: { xs: '1rem', sm: '1.125rem' } } : undefined}
              >
                {item.folder_path || `학습 ${index + 1}`}
              </Typography>
              {item.cctv_list && Array.isArray(item.cctv_list) && item.cctv_list.length > 0 && (
                <Chip
                  label={`${item.cctv_list.length}개`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>
          }
          secondary={
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="caption"
                display="block"
                color="text.primary"
                sx={variant === 'mobile' ? { fontSize: { xs: '0.75rem', sm: '0.875rem' } } : undefined}
              >
                {item.started_at ?
                  new Date(item.started_at).toLocaleString('ko-KR') :
                  '시간 정보 없음'
                }
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  ...(variant === 'mobile' ? { fontSize: { xs: '0.7rem', sm: '0.75rem' } } : {})
                }}
              >
                학습률: {item.learning_rate || 'N/A'} | 반복: {item.epoch || 'N/A'} | 임계값: {item.var_threshold || 'N/A'}
              </Typography>
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};

interface ParkingHistoryTabProps {
  state: ParkingTestState;
  selectedHistory: any | null;
  historyLoading: boolean;
  historyResultLoading: boolean;
  onHistorySelect: (item: any) => void;
  projectId: string;
}

export const DesktopHistoryTab: React.FC<ParkingHistoryTabProps> = ({
  state,
  selectedHistory,
  historyLoading,
  historyResultLoading,
  onHistorySelect,
  projectId,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ display: 'flex', gap: 2, height: '70vh' }}>
      {/* 히스토리 목록 */}
      <Paper
        className="glass-medium animate-fade-in"
        sx={{
          flex: '0 0 300px',
          boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ ...responsiveSpacing.cardPadding }}>
          <Typography variant="h6" gutterBottom sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' },
            fontWeight: 600
          }}>
            학습 히스토리
          </Typography>

          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : state.learningHistory && state.learningHistory.length > 0 ? (
            <List sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {state.learningHistory.map((item, index) => (
                <HistoryListItem
                  key={item.id || index}
                  item={item}
                  index={index}
                  isSelected={selectedHistory?.folder_path === item.folder_path}
                  onSelect={onHistorySelect}
                  variant="desktop"
                />
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              학습 히스토리가 없습니다. (현재 개수: {state.learningHistory?.length || 0})
            </Typography>
          )}
        </Box>
      </Paper>

      {/* 히스토리 결과 */}
      <Box sx={{ flex: 1 }}>
        {historyResultLoading ? (
          <Paper
            className="glass-medium"
            sx={{
              height: '100%',
              boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" color="text.secondary">
                학습 결과를 불러오는 중...
              </Typography>
            </Box>
          </Paper>
        ) : selectedHistory && state.selectedHistoryResults ? (
          <Paper
            className="glass-medium animate-fade-in-up"
            sx={{
              height: '100%',
              boxShadow: isDark ? SHADOWS.dark.md : SHADOWS.light.md,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {selectedHistory.folder_path || '학습 결과'} - {selectedHistory.started_at ? new Date(selectedHistory.started_at).toLocaleString() : ''}
              </Typography>
              <LearningResultsView
                projectId={projectId}
                folderPath={selectedHistory.folder_path}
                cctvList={state.selectedHistoryResults.cctv_list}
                timestamp={state.selectedHistoryResults.timestamp}
              />
            </Box>
          </Paper>
        ) : (
          <Paper
            className="glass-medium"
            sx={{
              height: '100%',
              boxShadow: isDark ? SHADOWS.dark.sm : SHADOWS.light.sm,
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                히스토리를 선택하면 결과를 확인할 수 있습니다.
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

interface MobileHistoryDialogProps {
  open: boolean;
  onClose: () => void;
  state: ParkingTestState;
  selectedHistory: any | null;
  historyLoading: boolean;
  historyResultLoading: boolean;
  onHistorySelect: (item: any) => void;
  projectId: string;
}

export const MobileHistoryDialog: React.FC<MobileHistoryDialogProps> = ({
  open,
  onClose,
  state,
  selectedHistory,
  historyLoading,
  historyResultLoading,
  onHistorySelect,
  projectId,
}) => {
  const theme = useTheme();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isSmallMobile}
      sx={{
        '& .MuiDialog-paper': {
          height: { xs: '100%', sm: '80vh' }
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">학습 히스토리</Typography>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : state.learningHistory && state.learningHistory.length > 0 ? (
          <List sx={{ pt: 0 }}>
            {state.learningHistory.map((item, index) => (
              <HistoryListItem
                key={item.id || index}
                item={item}
                index={index}
                isSelected={selectedHistory?.folder_path === item.folder_path}
                onSelect={onHistorySelect}
                variant="mobile"
              />
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              학습 히스토리가 없습니다.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              첫 학습을 실행해보세요.
            </Typography>
          </Box>
        )}

        {/* 선택된 히스토리 결과 */}
        {selectedHistory && state.selectedHistoryResults && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
              {selectedHistory.folder_path || '학습 결과'} - {selectedHistory.started_at ? new Date(selectedHistory.started_at).toLocaleDateString() : ''}
            </Typography>
            <LearningResultsView
              projectId={projectId}
              folderPath={selectedHistory.folder_path}
              cctvList={state.selectedHistoryResults.cctv_list}
              timestamp={state.selectedHistoryResults.timestamp}
            />
          </Box>
        )}

        {historyResultLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              결과 로딩 중...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          sx={{ ...touchFriendly.button }}
        >
          닫기
        </Button>
      </DialogActions>
    </Dialog>
  );
};
